import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Divider,
  Modal as MuiModal,
  SxProps,
  Typography,
} from '@mui/material';
import { F0 } from '../../helpers/types';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { PropsWithChildren, useState } from 'react';
import { createPortal } from 'react-dom';
import { Variant } from '@mui/material/styles/createTypography';

export const useModal = <T,>(
  Elem: (props: T) => any,
  { onClose: onCloseCallback }: { onClose?: () => void } = {},
) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = () => setIsOpen(true);
  const close = () => {
    setIsOpen(false);
    onCloseCallback?.();
  };

  const Popup: any = (props: Omit<T, 'onClose'>) =>
    isOpen
      ? createPortal(
          <Elem onClose={close} {...(props as any)} />,
          document.body,
        )
      : null;

  return { isOpen, open, close, Popup };
};

export type ModalProps = PropsWithChildren<{
  onClose: F0;
  isOpen: boolean;
  title?: string;
  height?: string;
  sx?: SxProps;
  hideClose?: boolean;
  centerTitle?: boolean;
  disableBackdropClick?: boolean;
  titleVariant?: Variant;
}>;
const truncateStyle: SxProps = {
  width: { sm: '90vw', md: '50vw' },
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const Modal = (p: ModalProps) => (
  <PlainModal {...p}>
    <div className="flex justify-between p-4 pl-6 pr-6">
      {p.title && (
        <Typography
          variant={p?.titleVariant || 'h2'}
          sx={{
            ...truncateStyle,
            ...(p.centerTitle ? { textAlign: 'center' } : {}),
          }}
        >
          {p.title}
        </Typography>
      )}
      {!p.hideClose && (
        <div className="cursor-pointer flex " onClick={p.onClose} role="button">
          <FontAwesomeIcon
            icon={faTimes}
            className="ml-3 leading-none text-sm w-[28px] h-[28px] self-center"
          />
        </div>
      )}
    </div>
    {p.title && <Divider />}
    <Box
      className="p-6 overflow-y-scroll"
      sx={{ height: p.height || '58vh', maxHeight: p.height || '58vh' }}
    >
      {p.children}
    </Box>
  </PlainModal>
);

export const PlainModal = (p: ModalProps) => {
  const handleClose = (
    event: any,
    reason: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (!!p.disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    p.onClose();
  };
  return (
    <MuiModal open={p.isOpen} onClose={handleClose}>
      <Box
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full sm:w-1/2 flex flex-col outline-none bg-white shadow-2xl rounded-xl"
        sx={{ height: p.height || '66vh', maxHeight: '66vh', ...(p.sx || {}) }}
      >
        {p.children}
      </Box>
    </MuiModal>
  );
};
